import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  SeasonEligibility,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SelectSeasonality } from 'atoms/SelectSeasonality';
import type { SeasonEligibilityOptions } from 'lib/seasonalities';
import {
  ALL_VALUE,
  seasonEligibilityOptions,
  seasonEligibilityOptionsMessages,
} from 'lib/seasonalities';

const Label = styled.span`
  display: flex;
  align-items: center;
  gap: var(--unit);
`;

const seasonEligibilityIcons = {
  [So5LeaderboardSeasonality.ALL_SEASONS]: (
    <SportSeasonalityIcon
      size={1.5}
      seasonality={So5LeaderboardSeasonality.ALL_SEASONS}
    />
  ),
  [SeasonEligibility.IN_SEASON]: (
    <SportSeasonalityIcon
      size={1.5}
      seasonality={SeasonEligibility.IN_SEASON}
    />
  ),
  [SeasonEligibility.CLASSIC]: (
    <SportSeasonalityIcon size={1.5} seasonality={SeasonEligibility.CLASSIC} />
  ),
  [ALL_VALUE]: <FormattedMessage {...glossary.all} />,
};

type Props = {
  seasonality: SeasonEligibilityOptions;
  setSeasonality: (value: SeasonEligibilityOptions) => void;
  seasonalities?: SeasonEligibilityOptions[];
  forceDesktopLayout?: boolean;
  withLabel?: boolean;
  onlyGrowOnSelected?: boolean;
};

export const TextSwitchSeasonality = ({
  seasonality,
  seasonalities,
  setSeasonality,
  forceDesktopLayout,
  withLabel,
  onlyGrowOnSelected,
}: Props) => {
  const options = seasonalities || seasonEligibilityOptions;
  const { up: upToTablet } = useScreenSize('tablet');
  if (upToTablet || forceDesktopLayout) {
    return (
      <TextSwitch
        options={options.map(option => ({
          label: (
            <Tooltip
              skip={withLabel || option === ALL_VALUE}
              title={
                option !== ALL_VALUE && (
                  <FormattedMessage
                    {...seasonEligibilityOptionsMessages[option]}
                  />
                )
              }
            >
              <Label>
                {seasonEligibilityIcons[option]}
                {!!withLabel && option !== ALL_VALUE && (
                  <FormattedMessage
                    {...seasonEligibilityOptionsMessages[option]}
                  />
                )}
              </Label>
            </Tooltip>
          ),
          value: option,
        }))}
        value={seasonality}
        onChange={setSeasonality}
        compact
        onlyGrowOnSelected={onlyGrowOnSelected}
      />
    );
  }
  return (
    <SelectSeasonality
      seasonalityOptions={seasonEligibilityOptions}
      seasonality={seasonality}
      setSeasonality={setSeasonality}
      withoutLabel
      buttonSize="compact"
    />
  );
};
